import { IMG } from "@/lib/site";
import SectionHeader from "@/components/site/SectionHeader";
import FaqList from "@/components/site/FaqList";
import Seo from "@/components/site/Seo";
import { PAGE_SEO, breadcrumbLd, faqLd } from "@/lib/seo";

const groups = [
  {
    title: "Understanding Water Cremation",
    items: [
      {
        q: "What is water cremation?",
        a: "Water cremation is a gentle form of pet aftercare that uses warm water, gentle circulation, and a small amount of alkali to accelerate the natural process of returning the body to its basic organic elements. It is also commonly known as aqua cremation or alkaline hydrolysis.",
      },
      {
        q: "Is water cremation the same as aqua cremation or alkaline hydrolysis?",
        a: "Yes. The three terms describe the same process. Aqua Cremation is the most consumer-friendly name, while alkaline hydrolysis is the scientific term used in medical and veterinary settings.",
      },
      {
        q: "How long does the process take?",
        a: "The water cremation process itself typically takes several hours. Once the entire journey from transfer to processing is complete, families generally receive their pet's remains within one to two weeks, depending on the provider.",
      },
    ],
  },
  {
    title: "The Process & Care",
    items: [
      {
        q: "Are pets cremated individually?",
        a: "Most reputable water cremation providers offer individual aqua cremation services. Each pet is given a unique identifier used throughout the entire process to ensure the remains returned are unmistakably your own pet's.",
      },
      {
        q: "What remains are returned to the family?",
        a: "The remains returned are clean mineral remains, typically lighter in colour and finer in texture than ash from flame cremation. Many families describe them as softer and more delicate, similar to fine sand.",
      },
      {
        q: "Are there any emissions during the process?",
        a: "No. Water cremation produces no airborne emissions, no smoke, and no particulate matter. The water used is treated and returned to the local water system in compliance with strict environmental and health regulations.",
      },
    ],
  },
  {
    title: "Environmental Impact",
    items: [
      {
        q: "Is water cremation environmentally friendly?",
        a: "Many families consider water cremation to be one of the more environmentally conscious pet aftercare options available, due to its lower energy consumption, reduced carbon footprint, and absence of airborne emissions.",
      },
      {
        q: "How does the carbon footprint compare to flame cremation?",
        a: "Water cremation uses significantly less energy than flame cremation, often estimated at around 90 percent less. It also avoids the natural gas combustion required to maintain the high temperatures used in flame cremation.",
      },
    ],
  },
  {
    title: "Availability & Cost",
    items: [
      {
        q: "Is water cremation available throughout Australia?",
        a: "Availability is still developing across Australia. Queensland currently leads in availability through providers such as Pure Souls Pet Aqua Cremation in Townsville. Interest in NSW and VIC continues to grow.",
      },
      {
        q: "Does water cremation cost more than flame cremation?",
        a: "Pricing varies by provider and the memorial options chosen. Many families find water cremation comparable to premium flame cremation services. Speak directly with a provider for accurate pricing details.",
      },
    ],
  },
];

export default function Faq() {
  return (
    <div data-testid="faq-page">
      <Seo
        {...PAGE_SEO.faq}
        jsonLd={[
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "FAQ", path: "/faq" },
          ]),
          faqLd(groups.flatMap((g) => g.items)),
        ]}
      />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={IMG.texSage} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#F9F9F6]/85" />
        </div>
        <div className="container-editorial pt-20 pb-16">
          <div className="overline mb-5">Frequently Asked</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.05] text-forest-900 max-w-4xl">
            Calm, clear answers to common questions.
          </h1>
          <p className="mt-6 max-w-2xl text-lg sm:text-xl text-forest-700 leading-[1.65]">
            The questions families ask most often as they begin to explore water
            cremation for their pet.
          </p>
        </div>
      </section>

      <section className="container-editorial section-padding">
        <div className="space-y-20">
          {groups.map((g, gi) => (
            <div key={g.title} data-testid={`faq-group-${gi}`}>
              <SectionHeader overline={`Section ${gi + 1}`} title={g.title} />
              <FaqList items={g.items} testIdPrefix={`faq-group-${gi}`} />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
